import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/modules/lib/utils"

const badgeVariants = cva(
  cn(
    "group/badge inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-2xl whitespace-nowrap transition-all",
    "has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5",
    "aria-invalid:border aria-invalid:border-borderError aria-invalid:ring-borderError/20",
    "[&>svg]:pointer-events-none",
  ),
  {
    variants: {
      // Those variants are the same as the counterVariants
      variant: {
        positiveSubtle: "bg-(--badge-positive-bg-subtle) text-(--badge-positive-text-subtle)",
        positiveIntense: "bg-(--badge-positive-bg-intense) text-(--badge-positive-text-intense)",
        negativeSubtle: "bg-(--badge-negative-bg-subtle) text-(--badge-negative-text-subtle)",
        negativeIntense: "bg-(--badge-negative-bg-intense) text-(--badge-negative-text-intense)",
        noticeSubtle: "bg-(--badge-notice-bg-subtle) text-(--badge-notice-text-subtle)",
        noticeIntense: "bg-(--badge-notice-bg-intense) text-(--badge-notice-text-intense)",
        informationSubtle: "bg-(--badge-information-bg-subtle) text-(--badge-information-text-subtle)",
        informationIntense: "bg-(--badge-information-bg-intense) text-(--badge-information-text-intense)",
        neutralSubtle: "bg-(--badge-neutral-bg-subtle) text-(--badge-neutral-text-subtle)",
        neutralIntense: "bg-(--badge-neutral-bg-intense) text-(--badge-neutral-text-intense)",
        primarySubtle: "bg-(--badge-primary-bg-subtle) text-(--badge-primary-text-subtle)",
        primaryIntense: "bg-(--badge-primary-bg-intense) text-(--badge-primary-text-intense)",
        transparent: "bg-black-a300 text-(--badge-neutral-text-intense)",
      },
      size: {
        sm: "h-4 px-1 label-extra-small leading-2 [&>svg]:size-2",
        md: "h-5 px-2 label-small [&>svg]:size-3",
        lg: "h-6 px-3 label-medium [&>svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "neutralSubtle",
      size: "md",
    },
  },
)

function Badge({
  className,
  variant = "neutralSubtle",
  size = "md",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant, size }), className),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}
type BadgeVariant = VariantProps<typeof badgeVariants>["variant"]

export { Badge, badgeVariants, type BadgeVariant }
